/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractFormComponent,
  BuilderNavigationService,
  CommonActions,
  CommunicationEntryPoint,
  EmailAttachment,
  EmailDialogConfig,
  emailDialogRoute,
  FormActionPipelines,
  FormFlowState,
  PrinterBusinessService,
  whenFormInvalid,
} from '@gh/builder-shared';
import {
  AelEvents,
  CommunicationType,
  ContactDeliveryMethod,
  createLinkFromAttachment,
  getAelEventsArray,
} from '@gh/builder-shared-data';
import { textMessageDialogRoute } from '@gh/common-internal-shared';
import {
  ActionFlow,
  AppRouter,
  compoundLogicalAction,
  eitherAction,
  exitAction,
  FormStateSnapshot,
  getRouteInitData,
  IS_REGION_AU_OR_NZ,
  isProducingStatusFinishEvent,
  LogicalAction,
  pollProducingStatus,
  queryRecalculationNeeded,
  sideEffectAction,
  streamValueAction,
} from '@gh/common-shared';
import { ProduceStatus } from '@gh/common-shared-data';
import { RouteDataService } from '@gh/core-data';
import { isMessageBasket } from '@gh/core-messages';
import { TranslationService } from '@gh/core-mls';
import {
  DialogService,
  NotificationService,
  TextMessageDialogConfig,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { createLink } from '@gh/core-util';
import { of$, throwError$ } from '@gh/rx';
import { catchError$, filter$, map$, mapTo$, skip$, switchMap$ } from '@gh/rx/operators';
import { compact, isEmpty, noop } from 'lodash';
import { Observable, Subscription } from 'rxjs';
import { DrawDetailsBusinessService } from '../../../resources/draw-details-business.service';
import {
  DRAW_DETAILS_INIT,
  DrawCalculateResponse,
  DrawDetails,
  DrawDetailsLendersInfo,
  DrawDetailsLendersScheduleItem,
  DrawDetailsPaymentInfo,
  DrawDetailsPaymentProgressInfo,
  DrawDetailsPaymentTab,
  getDrawLendersScheduleItemId,
  mergeDrawDetailsAmountWithGst,
} from '../../../resources/draw-details.model';
import { DrawDetailsFormService } from '../draw-details-form.service';
import { DrawDetailsPaymentProgressFormComponent } from '../draw-details-payment-progress-form/draw-details-payment-progress-form.component';

type DrawFormFlowState = FormFlowState<DrawDetails> & {
  calculateResponse?: DrawCalculateResponse;
  produceStatus: ProduceStatus;
};

@Component({
             selector: 'gh-draw-details-make-draw',
             template: require('./draw-details-make-draw-tab.component.html'),
             providers: [DrawDetailsFormService],
           })
export class DrawDetailsMakeDrawTabComponent extends AbstractFormComponent<DrawDetails> implements OnInit, OnDestroy {
  produceLabel$ = this.form.produceStatus$.pipe(map$(({ processed, total }) => ({
    id: 'lbl_draw_docs_in_progress',
    params: { totalProduced: processed, totalAll: total },
  })));
  isCloud: boolean;
  processEnabledWhen$ = this.form.isLendersSchedule$.pipe(
    map$((isLendersSchedule) => isLendersSchedule ? 'dirty' : 'always'));

  // tslint:disable-next-line:max-line-length
  @ViewChild(DrawDetailsPaymentProgressFormComponent, {static: true}) progressForm?: DrawDetailsPaymentProgressFormComponent;

  private produceSubscription: Subscription;

  get title(): string {
    return this.form.isCreateMode ? 'lbl_make_draw' : 'lbl_view_draw';
  }

  constructor(private route: ActivatedRoute,
              private translations: TranslationService,
              private router: AppRouter,
              private routeData: RouteDataService,
              private printerBusiness: PrinterBusinessService,
              private waitings: WaitingService,
              private dialogs: DialogService,
              private notifications: NotificationService,
              private navigations: BuilderNavigationService,
              private business: DrawDetailsBusinessService,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              public form: DrawDetailsFormService) {
    super();
  }

  get hasRecalculateButton(): boolean {
    return IS_REGION_AU_OR_NZ;
  }

  get completeFlow(): ActionFlow<DrawFormFlowState> {
    return this.pipelines
               .complete<DrawFormFlowState>({
      validateForm: this.commonActions.validateWithEffect(
        whenFormInvalid, () => this.handleErrors(),
      ),
      execute: this.saveAction,
      finish: (({ produceStatus }) => {
        this.form.resetDirty();
        this.form.setProducingStatus(produceStatus);
      }),
    });
  }

  get saveAction(): LogicalAction<DrawFormFlowState> {
    return compoundLogicalAction('save.success', 'save.fail', {
      // recalculate to actualize amountWithGst (only if recalculation needed)
      'start': eitherAction(({ value }: DrawFormFlowState) => queryRecalculationNeeded(value))
        .bindAs('recalculate', 'checkThisDraw?'),
      // tslint:disable-next-line:max-line-length
      'recalculate': this.commonActions.async((state: DrawFormFlowState) =>
        this.business.calculateGst(state.value)
            .pipe(map$(
              (calculateResponse) => ({ ...state, calculateResponse }),
                  )))
                         .bindAs('recalculate.success', 'fail'),
      // merge recalculated value
      // tslint:disable-next-line:no-non-null-assertion
      'recalculate.success': sideEffectAction(({ calculateResponse }) => this.form.setAmountWithGst(calculateResponse!))
        .bindAs('checkThisDraw?'),
      // after recalculate => check if thisDraw is negative
      'checkThisDraw?': eitherAction(() => this.form.thisDraw < 0).bindAs('confirmNegative', 'create'),
      'confirmNegative': this.commonActions.confirmation(yesNoConfirmation('msg_process_with_negative_amount'), {
        yes: 'create',
        no: 'fail',
      }),
      'create': this.createInvoiceAction.bindAs('success', 'fail'),
      'success': exitAction('save.success'),
      '*': exitAction('save.fail'),
    });
  }

  get createInvoiceAction(): LogicalAction<DrawFormFlowState> {
    return streamValueAction((state: DrawFormFlowState) =>
     this.business.calculateGst(state.value).pipe(
       map$((response) => mergeDrawDetailsAmountWithGst(state.value, response)),
       switchMap$((details) =>
                    this.business.create(
                      details,
                      this.progressForm && this.progressForm.historyHtmlContent)),
       catchError$((errors) =>
                     // continue processing if producing was already started (by another user, in another tab or browser)
                     isMessageBasket(errors) && errors.has('msg_draw_docs_in_producing')
                       ? [void 0] : throwError$(errors)),
       switchMap$(() => this.business.getProduceStatus(this.form.jobId)),
       (this.waitings.operator()),
       map$((produceStatus) => ({ ...state, produceStatus }))));
  }

  ngOnInit(): void {
    const { details, isCreateMode } = getRouteInitData(this.route);
    this.form.build(details, isCreateMode);
    this.isCloud = details.isCloud;

    this.route.params.pipe(
      skip$(1),
      switchMap$(({ jobId }) =>
                   jobId ? this.business.getForJob(jobId).pipe(
                     (this.waitings.operator()),
                     catchError$((basket) => {
                       this.updateJob();
                       this.notifications.processAll(basket);
                       return [];
                     })) : [DRAW_DETAILS_INIT]))
        .subscribe(
          (detailsForJob) => {
            this.isCloud = detailsForJob.isCloud;
          });

    this.produceSubscription = pollProducingStatus({
       status: this.form.produceStatus$,
       poll: () => this.business.getProduceStatus(
         this.form.jobId,
         this.form.currentInvoiceId,
       ),
      }).subscribe((event) => {
      this.form.setProducingStatus(event.nextStatus);

      if (isProducingStatusFinishEvent(event)) {
        this.finishProducing();
      }
    });
  }

  ngOnDestroy(): void {
    this.produceSubscription.unsubscribe();
  }

  onPaymentInfoChange(info: FormStateSnapshot<DrawDetailsPaymentInfo>): void {
    this.form.setPaymentInfo(info);
    this.business.calculateGst(this.form.value).pipe(
      this.waitings.operator())
        .subscribe(
          (response) => this.form.setAmountWithGst(response),
          this.notifications.processAll);
  }

  onPaymentProgressInfoChange(info: FormStateSnapshot<DrawDetailsPaymentProgressInfo>): void {
    this.form.setPaymentProgressInfo(info);
  }

  onLendersInfoChange(info: FormStateSnapshot<DrawDetailsLendersInfo>): void {
    this.form.setLendersInfo(info);
  }

  onLendersItemThisDrawChange(item: DrawDetailsLendersScheduleItem, thisDraw?: number): void {
    this.form.setLendersItemThisDraw(getDrawLendersScheduleItemId(item), thisDraw);
  }

  onLendersFocusedCellChange(): void {
    this.form.resetLendersScheduleFocusedCell();
  }

  onPreviewChange(isPreview: boolean): void {
    this.form.setIsPreview(isPreview);
  }

  selectPaymentTab(tab: DrawDetailsPaymentTab): void {
    this.form.selectPaymentTab(tab);
  }

  onRecalculate(): void {
    if (this.form.isPaymentProgressInfoInvalid) {
      this.form.highlightPaymentProgressInfo();
      this.form.selectPaymentTab(DrawDetailsPaymentTab.Progress);
      return;
    }

    this.business.calculateGst(this.form.value).pipe(
      this.waitings.operator())
        .subscribe(
          (response) => this.form.setAmountWithGst(response),
          this.notifications.processAll);
  }

  onPrint(): void {
    const { jobId, currentInvoiceId, isPreview } = this.form;
    this.business.produceDocs(jobId, currentInvoiceId!, isPreview)// tslint:disable-line:no-non-null-assertion
        .pipe(
              catchError$((errors) => // continue processing if producing was already started (by another user, in another tab or browser)
                            isMessageBasket(errors) && errors.has('msg_draw_docs_in_producing')
                              ? [void 0] : throwError$(errors)),
              switchMap$(() => this.business.getProduceStatus(jobId, currentInvoiceId)),
              this.waitings.operator())
        .subscribe(
          (produceStatus) => this.form.setProducingStatus(produceStatus),
          this.notifications.processAll);
  }

  goToAel(): void {
    this.navigations.goToAel({
                               entityId: this.form.jobId,
                               entityType: 'JOB',
                               eventTypeIds: getAelEventsArray(AelEvents.Job.ProgressDraw)
                                 .concat(getAelEventsArray(AelEvents.Job.ChangeOrderDraw)),
                               label: 'lbl_make_draw',
                             });
  }

  onCancel(): void {
    const flow = this.deactivateFlow;
    flow.run({
               trigger: 'deactivate',
               form: this.form,
               value: this.form.submitValue,
             }).toFinalState().pipe(
      filter$(({ confirmation }) => Boolean(confirmation)))
        .subscribe(() => this.updateJob());
  }

  private confirmJobChange(): Observable<boolean> {
    if (this.form.dirty) {
      return this.dialogs.confirmation(yesNoConfirmation('msg_changes_will_be_lost')).pipe(
        filter$(yesAnswer),
        mapTo$(true));
    } else {
      return of$(true);
    }
  }

  private updateJob(): void {
    this.router.navigateByUrl(`/accounting/draws/${this.form.jobId}`);
    // return this.routeData.mergeQueryParams({ jobId: void 0 });
  }

  private finishProducing(): void {
    this.business.getGeneratedDocuments(this.form.jobId, this.form.currentInvoiceId).pipe(
      switchMap$(({ docs, preview }) => {
        if (isEmpty(docs)) {
          return [];
        } else if (this.form.isCreateMode) {
          // for MAKE_DRAW entrypoint
          if (preview) {
            return this.showDocsDialog(docs).pipe(switchMap$(() => this.deliverDocs(docs)));
          } else {
            return this.deliverDocs(docs);
          }
        } else {
          // for VIEW_DRAW entrypoint
          if (preview) {
            return this.showDocsDialog(docs);
          } else {
            return this.deliverDocs(docs);
          }
        }
      }))
        .subscribe(
          noop,
          this.notifications.processAll,
          () => this.updateJob());
  }

  private handleErrors(): void {
    if (this.form.isLendersSchedule) {
      if (this.form.isLendersInfoInvalid) {
        this.form.highlightLendersInfo();
      } else if (this.form.isLendersScheduleInvalid) {
        this.notifications.error('msg_he_incorrect_data_in_tables');
        this.form.navigateToInvalidLenderScheduleCell();
      }
    } else {
      if (this.form.isRequiredTabNotVisited) {
        this.notifications.error('msg_view_both_pages');
        const { tabs: { isChangeOrdersVisited } } = this.form.value;
        if (!isChangeOrdersVisited) {
          this.form.selectPaymentTab(DrawDetailsPaymentTab.ChangeOrders);
        } else {
          this.form.selectPaymentTab(DrawDetailsPaymentTab.Progress);
        }
      }

      if (this.form.isPaymentInfoInvalid) {
        this.form.highlightPaymentInfo();
      }
      if (this.form.isPaymentProgressInfoInvalid) {
        this.form.highlightPaymentProgressInfo();
        this.form.selectPaymentTab(DrawDetailsPaymentTab.Progress);
      }
    }
  }

  // tslint:disable-next-line:max-line-length
  private showDocsDialog(docs: EmailAttachment[], noPrinterExist: boolean = false, noSelectedPrinterExist: boolean = false): Observable<void> {
    return this.dialogs.openWithInput(textMessageDialogRoute, <TextMessageDialogConfig<string>>{
      title: this.translations.get('lbl_draw_docs'),
      html: compact([
                      !noPrinterExist ?
                        (noSelectedPrinterExist ? this.translations.get('lbl_selected_printer_no_available') : void 0) :
                        this.translations.get('lbl_printer_no_available'),
                      this.translations.get('lbl_draw_docs_links'),
                      docs.map(createLinkFromAttachment).join('<br>'),
                    ]).join('<br>'),
      buttons: [
        { title: this.translations.get('lbl_ok'), value: 'ok', type: 'primary' },
      ],
    }).afterClosed();
  }

  private deliverDocs(docs: EmailAttachment[]): any {
    const { jobId, customerId, customerEmail, customerMethod } = this.form.job;

    if (customerMethod === ContactDeliveryMethod.Mail || !this.form.isCreateMode) {
      if (this.isCloud) {
        return this.printerBusiness.choosePrinter().pipe(
          switchMap$(({ authCode, printer }) =>
                       this.business.print(jobId, {
                         // tslint:disable-next-line:no-non-null-assertion
                         authCode: authCode!, generatedDocs: docs, printerId: printer!.cloudPrinterId,
                       }).pipe((this.waitings.operator()))),
          catchError$((errors) => {
            // tslint:disable-next-line:max-line-length
            if (isMessageBasket(errors) && (errors.has('msg_no_printer')
                                            || errors.has('msg_printer_selected_not_exist'))) {
              this.notifications.processAll(errors);
              return this.showDocsDialog(docs, errors.has('msg_no_printer'),
                                         errors.has('msg_printer_selected_not_exist'));
            } else {
              return throwError$(errors);
            }
          }));
      } else {
        return this.business.print(jobId, {
          // tslint:disable-next-line:no-non-null-assertion
          authCode: '', generatedDocs: docs, printerId: '',
        }).pipe(
          this.waitings.operator(),
          switchMap$(({ s3Link }) => this.showReportDialog(s3Link)));
      }
    } else {
      return this.dialogs.openWithInput(emailDialogRoute, <EmailDialogConfig>{
        entryPoint: CommunicationEntryPoint.Job,
        contactId: customerId,
        jobId,
        collapseThreshold: 4,
        gFolderId: this.business.basicInfo.jobDocsFolderId,
        email: {
          jobId,
          contactId: customerId,
          email: customerEmail,
          subject: '',
          message: '',
          communicationType: CommunicationType.Email,
          attachments: docs,
        },
      })
                 .afterClosed();
    }
  }

  private showReportDialog(link: any): Observable<void> {
    return this.dialogs.openWithInput(textMessageDialogRoute, <TextMessageDialogConfig<string>>{
      title: this.translations.get('Link to Print the Draw document(s)'),
      html: [
        this.translations.get('Please follow the link below to print the Draw document(s)'),
        createLink(link, 'Your file'),
      ].join(' '),
      buttons: [{ value: 'ok', title: this.translations.get('lbl_ok'), type: 'primary' }],
    }).afterClosed();
  }
}
